import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useAction, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { formatMinor } from "@convex/payments/domain";
import { CheckCircle, XCircle, Clock, ArrowSquareOut, Gift, Receipt } from "@phosphor-icons/react";
import { getStoredUserSession } from "@shared/hooks/useUserSession";
import { trackEvent } from "@shared/lib/posthog-lazy";
import { brandify } from "../components/brand";
import { amountAriaLabel, formatPaidAt } from "../lib/money";

/**
 * Ecranul de plată al consumatorului (PRD §4.1, §5).
 *
 * Fără cont: ID-ul intenției e capabilitatea. Starea vine EXCLUSIV din
 * backend: întoarcerea din aplicația băncii (`?return=1`) nu înseamnă nimic
 * până nu confirmă providerul.
 */

export const Route = createFileRoute("/pay/$intentId")({
  head: () => ({
    meta: [{ title: "Plată — suuper" }, { name: "robots", content: "noindex, nofollow" }],
  }),
  validateSearch: (search: Record<string, unknown>): { return?: 1 } =>
    search.return === "1" || search.return === 1 ? { return: 1 } : {},
  component: PayPage,
});

const ERROR_TEXT: Record<string, string> = {
  providerUnavailable: "RoPay este temporar indisponibil. Încearcă din nou.",
  providerRejected: "Banca a respins cererea de plată. Cere comerciantului o plată nouă.",
  ropayDisabled: "Plata prin RoPay este temporar indisponibilă.",
  paymentsDisabled: "Plata prin RoPay este temporar indisponibilă.",
  partnerNotAllowed: "Acest comerciant nu poate încasa încă prin suuper.",
  noPaymentConnection: "Acest comerciant nu poate încasa încă prin suuper.",
  tooManyAttempts: "Prea multe încercări. Cere comerciantului o plată nouă.",
  notAuthorizable: "Această plată nu mai poate fi pornită.",
  notFound: "Această plată nu există.",
  inProgress: "Se pregătește plata…",
};

function PayPage() {
  const { intentId } = Route.useParams();
  const { return: returned } = Route.useSearch();
  const intent = useQuery(api.payments.intents.getPaymentIntent, { intentId });
  const authorize = useAction(api.payments.intents.authorizePayment);

  const [paying, setPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const retried = useRef(false);
  const trackedStatus = useRef<string | null>(null);

  const status = intent === undefined ? undefined : intent === null ? null : intent.status;

  // Evenimente de produs: o dată per stare afișată, nu la fiecare randare.
  useEffect(() => {
    if (status === undefined || trackedStatus.current === (status ?? "missing")) return;
    trackedStatus.current = status ?? "missing";
    void trackEvent("payment_page_viewed", { status: status ?? "missing" });
    if (status === "SUCCEEDED") {
      void trackEvent("payment_success_screen_viewed");
      if (intent?.reward) void trackEvent("reward_viewed");
    }
  }, [status, intent?.reward]);

  const startPayment = async () => {
    if (paying || !intent) return;
    setPaying(true);
    setError(null);
    void trackEvent("pay_button_clicked");
    try {
      const { userId, sessionToken } = getStoredUserSession();
      const consumer = userId && sessionToken ? { userId, sessionToken } : undefined;
      const result = await authorize({ intentId, consumer });
      if (result.ok) {
        void trackEvent("bank_handoff_started");
        window.location.assign(result.authorizationUrl);
        return;
      }
      if (result.error === "inProgress" && !retried.current) {
        // Altă cerere tocmai vorbește cu banca; o singură reîncercare, după o secundă.
        retried.current = true;
        setError(ERROR_TEXT.inProgress);
        setTimeout(() => {
          setPaying(false);
          void startPayment();
        }, 1000);
        return;
      }
      setError(ERROR_TEXT[result.error] ?? "A apărut o eroare. Încearcă din nou.");
    } catch (err) {
      console.error("[pay] autorizare eșuată", err);
      setError("A apărut o eroare. Încearcă din nou.");
    }
    setPaying(false);
  };

  if (intent === undefined) {
    return (
      <Shell>
        <div className="w-10 h-10 mx-auto animate-spin motion-reduce:animate-none rounded-full border-4 border-primary border-t-transparent" />
      </Shell>
    );
  }

  if (intent === null) {
    return (
      <Shell>
        <XCircle size={48} className="mx-auto text-text-muted mb-3" />
        <p className="text-lg font-semibold text-text" data-testid="payment-status">
          Această plată nu există.
        </p>
      </Shell>
    );
  }

  const amount = formatMinor(intent.amountMinor, "ro");
  const newPaymentLink =
    intent.createdBy === "consumer" && intent.locationSlug ? `/m/${intent.locationSlug}` : null;

  return (
    <Shell>
      <header className="text-center mb-6">
        <p className="text-sm text-text-muted mb-1">Plătești</p>
        <p className="text-4xl font-bold text-text tracking-tight" data-testid="payment-amount" aria-label={amountAriaLabel(intent.amountMinor)}>
          {amount}
        </p>
        <p className="text-sm text-text-muted mt-2">
          către{" "}
          <span className="font-semibold text-text" data-testid="merchant-name">
            {intent.merchantName}
          </span>
        </p>
        {intent.reference && <p className="text-xs text-text-light mt-1">{intent.reference}</p>}
      </header>

      <section aria-live="polite" className="bg-white rounded-2xl p-5 shadow-sm text-center">
        {intent.status === "CREATED" && (
          <>
            <p className="text-text mb-1" data-testid="payment-status">
              Confirmi plata în aplicația băncii tale, prin RoPay.
            </p>
            <Countdown expiresAt={intent.expiresAt} />
            <button
              type="button"
              onClick={startPayment}
              disabled={paying}
              data-testid="pay-button"
              className="mt-5 w-full py-4 bg-primary text-white font-semibold rounded-xl disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {paying ? "Se deschide banca…" : "Plătește cu RoPay"}
            </button>
            {error && (
              <p role="alert" className="mt-3 text-sm text-error">
                {error}
              </p>
            )}
          </>
        )}

        {intent.status === "AWAITING_AUTHORIZATION" && (
          <>
            <Spinner />
            <p className="font-semibold text-text mb-1" data-testid="payment-status">
              Confirmă plata în aplicația băncii.
            </p>
            <p className="text-sm text-text-muted">Pagina se actualizează singură când banca ne confirmă.</p>
            {intent.authorizationUrl && (
              <a
                href={intent.authorizationUrl}
                className="mt-4 inline-flex items-center gap-2 text-primary font-medium text-sm"
              >
                <ArrowSquareOut size={16} /> Deschide din nou aplicația băncii
              </a>
            )}
          </>
        )}

        {intent.status === "PENDING" && (
          <>
            <Spinner />
            <p className="font-semibold text-text mb-1" data-testid="payment-status">
              Așteptăm confirmarea plății.
            </p>
            <p className="text-sm text-text-muted">
              {returned ? "Plata este în curs de confirmare." : "Banca procesează plata; poate dura câteva secunde."}
            </p>
          </>
        )}

        {intent.status === "SUCCEEDED" && (
          <>
            <CheckCircle size={56} weight="fill" className="mx-auto text-success mb-2" />
            <p className="text-xl font-bold text-text mb-1" data-testid="payment-status">
              Plata a fost confirmată.
            </p>
            <p className="text-sm text-text-muted">
              {amount} către {intent.merchantName}
            </p>
            {intent.succeededAt && <p className="text-xs text-text-light mt-1">{formatPaidAt(intent.succeededAt)}</p>}
            {intent.receipt && (
              <p className="mt-3 text-sm text-text inline-flex items-center gap-1.5" data-testid="receipt-link">
                <Receipt size={16} />
                {getStoredUserSession().userId ? (
                  <Link to="/app/receipts/$receiptId" params={{ receiptId: intent.receipt.id }} className="underline">
                    Chitanța {intent.receipt.number}
                  </Link>
                ) : (
                  <span>Chitanța {intent.receipt.number}</span>
                )}
              </p>
            )}
          </>
        )}

        {intent.status === "FAILED" && (
          <>
            <XCircle size={56} weight="fill" className="mx-auto text-error mb-2" />
            <p className="text-xl font-bold text-text mb-1" data-testid="payment-status">
              Plata nu a fost finalizată.
            </p>
            <p className="text-sm text-text-muted">Banca nu a confirmat plata. Nu s-au retras bani.</p>
            <RetryCta label="Încearcă din nou" link={newPaymentLink} />
          </>
        )}

        {intent.status === "EXPIRED" && (
          <>
            <Clock size={56} weight="fill" className="mx-auto text-text-muted mb-2" />
            <p className="text-xl font-bold text-text mb-1" data-testid="payment-status">
              Cererea de plată a expirat.
            </p>
            <RetryCta label="Cere o plată nouă" link={newPaymentLink} />
          </>
        )}

        {intent.status === "CANCELLED" && (
          <>
            <XCircle size={56} weight="fill" className="mx-auto text-text-muted mb-2" />
            <p className="text-xl font-bold text-text mb-1" data-testid="payment-status">
              Plata a fost anulată de comerciant.
            </p>
          </>
        )}
      </section>

      {intent.status === "SUCCEEDED" && intent.reward && (
        <section className="mt-4 bg-primary/10 border border-primary/20 rounded-2xl p-5" data-testid="reward-earned">
          <div className="flex items-start gap-3">
            <Gift size={28} weight="fill" className="text-primary flex-shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-text">
                {intent.reward.granted ? `Ai primit o vizită la ${intent.merchantName}` : `Vizitele tale la ${intent.merchantName}`}
              </p>
              <p className="text-sm text-text-muted mt-1">
                {intent.reward.progress} din {intent.reward.target} vizite până la: {intent.reward.label}
              </p>
              <div className="mt-2 h-2 bg-white rounded-full overflow-clip" aria-hidden="true">
                <div className="h-full bg-primary rounded-full" style={{ width: `${Math.min(100, (intent.reward.progress / intent.reward.target) * 100)}%` }} />
              </div>
              {intent.reward.available > 0 && (
                <p className="mt-2 text-sm font-semibold text-primary">Ai o recompensă disponibilă: {intent.reward.label}</p>
              )}
            </div>
          </div>
        </section>
      )}

      {intent.status === "SUCCEEDED" && !intent.reward && !getStoredUserSession().userId && (
        <p className="mt-4 text-center text-sm text-text-muted">
          Cu un cont {brandify("suuper")}, plățile îți aduc recompense.{" "}
          <Link to="/auth/register" className="text-primary font-medium underline">
            Creează cont
          </Link>
        </p>
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="min-h-screen bg-background px-4 py-8">
      <div className="max-w-md mx-auto">
        <p className="text-center text-xl font-bold mb-6">{brandify("suuper")}</p>
        {children}
      </div>
    </main>
  );
}

function Spinner() {
  return (
    <div
      className="w-10 h-10 mx-auto mb-3 animate-spin motion-reduce:animate-none rounded-full border-4 border-primary border-t-transparent"
      aria-hidden="true"
    />
  );
}

function RetryCta({ label, link }: { label: string; link: string | null }) {
  if (link) {
    return (
      <a href={link} className="mt-5 inline-block w-full py-3 bg-primary text-white font-semibold rounded-xl">
        {label}
      </a>
    );
  }
  return <p className="mt-4 text-sm text-text-muted">Cere comerciantului o plată nouă.</p>;
}

function Countdown({ expiresAt }: { expiresAt: number }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const remaining = Math.max(0, Math.floor((expiresAt - now) / 1000));
  return (
    <p className="text-xs text-text-light tabular-nums">
      Cererea expiră în {Math.floor(remaining / 60)}:{(remaining % 60).toString().padStart(2, "0")}
    </p>
  );
}
